import { useCallback, useEffect } from 'react';
import { IEnquiryForm, IEnquiryFormSchema } from '../model/IJsonSchema';
import enquiryForm from '../graphql/mutations/saveEnquiryForm';
import { ApolloError, useMutation } from '@apollo/client';
import {
  SaveEnquiryForm,
  SaveEnquiryForm_saveEnquiryForm_paymentInfo,
  SaveEnquiryFormVariables,
} from '../graphql/mutations/__generated__/SaveEnquiryForm';
import CONSTANTS from '../../../../common/constants/constants';
import useCheckoutContext from '../../../common/hooks/useCheckoutContext';
import usePlatformConfig from '../../../../common/react/usePlatformConfig';
import useNewDxpDigitalAssignmentEntryEvent from '../../../../gql/common/hooks/useNewDxpDigitalAssignmentEntryEvents';
import {
  computeMonthly,
  formatDate,
  getCheckoutStepsCommonFields,
  getEcommerceCommonFields,
  getEcommerceItemCommonFields,
  getEventDataCommonFields,
  getPurchaseOptionCommonFields,
  getRetailerCommonFields,
  getTradeInCommonFields,
  vproductCommonFields,
  removeHostNameFromUrl,
} from '../../../../gql/common/utils/dxpDataLayerCommonFields';
import {
  DxpDataLayerEventNames,
  FormContextTradeInType,
} from '../../../../../../common/utilities/customDatalayerInterface';
import { EventDataTypes, FinanceTypes } from '../../../../gql/common/enum/DataLayerEnums';
import useJourneyStepsContext from '../../../../gql/checkout4/hooks/useJourneyStepsContext';
import { FinanceFormType, JourneyType } from '../../../../gql/common/graphql/__generated__/globalTypes';
import { JourneyStepsRoutes } from '../../../../gql/checkout4/enum/JourneyStepsRoutes';

const useEnquiryFormSubmit = (
  handleAsyncValidationErrors: (error: ApolloError) => void,
  token: string,
  goToNextStep: (data: Record<string, SaveEnquiryForm_saveEnquiryForm_paymentInfo[]>) => void,
  stateId: string,
) => {
  const [saveForm, { loading, error }] = useMutation<SaveEnquiryForm, SaveEnquiryFormVariables>(enquiryForm.SAVE_FORM);
  const { setDeal } = useCheckoutContext();
  const {
    platformConfig: {
      platformFeatures: { dataLayer },
      fetchBreakdownJsonSchema,
      ga4DxpDataLayer,
    },
  } = usePlatformConfig();
  const { callDxpDigitalAssignmentEntryEvent } = useNewDxpDigitalAssignmentEntryEvent();
  const { jlrDataLayer } = ga4DxpDataLayer ?? {};
  const { steps, activeStep } = useJourneyStepsContext();
  const { deal } = useCheckoutContext();
  const userJourneyType = deal?.journeyType

  const removeTradeIn = useCallback((formData: IEnquiryFormSchema) => {
    delete formData.tradeIn;
    formData.tradeIn = {};
    formData.tradeIn.tradeInCheckbox = false;
  }, []);

  useEffect(() => {
    if (error) {
      handleAsyncValidationErrors(error);
    }
  }, [error]);

  const getNextUrl = useCallback(() => {
    const url = window.location.href;
    switch (deal?.journeyType) {
      case JourneyType.continue_in_dealership:
        return url.replace(JourneyStepsRoutes.YOUR_DETAILS, JourneyStepsRoutes.CONFIRMATION);
      case JourneyType.reserve_online:
        return url.replace(JourneyStepsRoutes.YOUR_DETAILS, JourneyStepsRoutes.PAYMENT);
      default:
        return url;
    }
  }, [deal?.journeyType]);

  const onSubmit = useCallback(
    ({ formData }: IEnquiryForm) => {
      (window as any).grecaptcha?.ready(() => {
        (window as any).grecaptcha
          .execute(CONSTANTS.RECAPTCHA_SITE_KEY, { action: 'submit' })
          .then(async (captchaToken: string) => {
            if (!formData?.tradeIn?.tradeInCheckbox) {
              removeTradeIn(formData);
            }
            const deal = await saveForm({
              variables: {
                dealId: token,
                state: stateId,
                captchaToken: captchaToken,
                data: { formData: JSON.stringify(formData) },
                dataLayer: !!dataLayer,
                fetchBreakdownJsonSchema: fetchBreakdownJsonSchema ?? true,
              },
            });

            if (!deal.errors) {
              setDeal(deal.data.saveEnquiryForm);
              goToNextStep({ previousTransactions: deal.data.saveEnquiryForm.paymentInfo });

              if (deal && formData) {
                const { attributes, finance, car } = deal.data.saveEnquiryForm || {};
                const { amount, currency } = car?.prices?.totalPrice?.[0]?.money || {};
                const { term, mileage } = finance?.formInput || {};
                const { reserveFee } = attributes?.paymentFee || {};
                const { tradeIn, appointment } = formData || {};

                callDxpDigitalAssignmentEntryEvent(
                  {
                    ...getEventDataCommonFields(DxpDataLayerEventNames.CONFIRM_RESERVATION_CTA_CLICK, true),
                    event_action: removeHostNameFromUrl(getNextUrl()),
                    event_category: EventDataTypes.CHECKOUT_CLICK,
                    event_label: `${EventDataTypes.BUTTON}::${EventDataTypes.SEND_ENQUIRY}`,
                    event_pagecategory: EventDataTypes.CHECKOUT,
                    click_url: removeHostNameFromUrl(getNextUrl()),
                    click_text: EventDataTypes.SEND_ENQUIRY,
                    click_name: EventDataTypes.BUTTON,
                    vproduct: {
                      ...vproductCommonFields(deal.data.saveEnquiryForm),
                      total_weight: '',
                      max_speed: '',
                      est_delivery_time: car?.deliveryTimeFrame?.toString() ?? '',
                    },
                    checkoutStep: {
                      ...getCheckoutStepsCommonFields(steps, activeStep),
                    },
                    purchaseOption: {
                      ...getPurchaseOptionCommonFields(deal.data.saveEnquiryForm),
                      vehicle_cash_price: car?.prices?.totalPrice?.[0]?.money
                        ? `${currency} ${amount}`.toLowerCase()
                        : '',
                      terms: term ? `${term} months` : '',
                      monthly_mileage: computeMonthly(mileage, 'km') ?? '',
                      reservation_fee: reserveFee?.[0].amount.toString() ?? '',
                      payment_period: finance?.simulation?.loanTermPeriod
                        ? `${finance.simulation.loanTermPeriod.value} ${finance.simulation.loanTermPeriod.unit}`
                        : '',
                      installment_fee: '',
                      down_payment_trade_in_price: '',
                      finance_product: finance?.simulation?.financeProductName
                        ? finance.simulation.financeProductName.toLowerCase()
                        : '',
                      final_deferred_payment: '',
                      total_to_pay: car?.prices?.totalPrice?.[0].money?.amount?.toString() ?? '',
                    },
                    tradein: {
                      ...getTradeInCommonFields(
                        deal.data.saveEnquiryForm,
                        tradeIn?.tradeInCheckbox ? (tradeIn as FormContextTradeInType) : undefined,
                      ),
                    },
                    retailer: {
                      ...getRetailerCommonFields(deal.data.saveEnquiryForm),
                    },
                    bookingDetails: {
                      date: appointment?.preferredDate ? formatDate(appointment?.preferredDate) : '',
                      time: (() => {
                        let time = appointment?.preferredTime ?? '';
                        if (
                          time &&
                          time !== 'no_preference'
                        ) {
                          time = time.replace(/^[^\d]+/, '');
                          if (/^\d{4}_\d{4}$/.test(time)) {
                            const [start, end] = time.split('_');
                            return `${start.slice(0, 2)}:${start.slice(2)}-${end.slice(0, 2)}:${end.slice(2)}`;
                          }
                          if (/^\d{2}:\d{2}_\d{2}:\d{2}$/.test(time)) {
                            return time.replace('_', '-');
                          }
                          if (/^\d{2}:\d{2}\.\d{2}:\d{2}$/.test(time)) {
                            return time.replace('.', '-');
                          }
                          return time;
                        }

                        return '';
                      })(),
                    },
                  },
                  jlrDataLayer,
                );

                callDxpDigitalAssignmentEntryEvent({ ecommerce: null }, jlrDataLayer);
                userJourneyType !== JourneyType.continue_in_dealership && callDxpDigitalAssignmentEntryEvent(
                  {
                    event: DxpDataLayerEventNames.ADD_PAYMENT_INFO,
                    ecommerce: {
                      ...getEcommerceCommonFields(deal.data.saveEnquiryForm),
                      payment_type: finance?.type === FinanceFormType.finance ? FinanceTypes.FINANCE : FinanceTypes.CASH,
                      items: [
                        {
                          ...getEcommerceItemCommonFields(deal.data.saveEnquiryForm),
                          price: car?.prices?.totalPrice?.[1]?.money?.amount.toString() ?? '',
                          quantity: '1',
                          value: getEcommerceCommonFields(deal.data.saveEnquiryForm).value,
                          payment_type: finance?.type === FinanceFormType.finance ? FinanceTypes.FINANCE : FinanceTypes.CASH,
                        },
                      ],
                    },
                  },
                  jlrDataLayer,
                );
              }
            } else {
              handleAsyncValidationErrors((deal.errors as unknown) as ApolloError);
            }
          });
      });
    },
    [token, stateId, steps, activeStep, jlrDataLayer, callDxpDigitalAssignmentEntryEvent],
  );

  return { isSubmitting: loading, onSubmit, error };
};

export default useEnquiryFormSubmit;
